import { Check, Lock } from "lucide-react";

/**
 * Shared horizontal progress/timeline rail — used by both the real
 * client Workspace Home (WorkspaceHome.jsx) and the coach's Journey
 * Configuration preview (Client360's JourneyConfigTab.jsx), so the
 * coach's preview is pixel-for-pixel the same component the client
 * actually sees, never a lookalike re-implementation.
 */
const CIRCLE_STYLE = {
  locked: "bg-white border-2 border-ink/15 text-ink-soft/50",
  available: "bg-white border-2 border-ink/20 text-ink-soft",
  current: "bg-terracotta border-2 border-terracotta text-paper",
  practised: "bg-ink border-2 border-ink text-paper",
  applied: "bg-ink border-2 border-ink text-paper",
  revisit: "bg-white border-2 border-mustard text-mustard-dark",
};
const LABEL_STYLE = { current: "text-terracotta", locked: "text-ink-soft/60" };
const LINE_DONE = "bg-ink";
const DONE_STATES = new Set(["practised", "applied"]);

export function JourneyRail({ nodes, onNodeClick, emptyLabel, testIdPrefix = "journey-rail" }) {
  if (!nodes || nodes.length === 0) {
    return (
      <div className="border border-ink/15 bg-white/30 rounded-lg p-6 text-ink-soft" data-testid={`${testIdPrefix}-empty`}>
        {emptyLabel || "No journey published yet."}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-1" data-testid={testIdPrefix}>
      <div className="flex items-start min-w-max px-1">
        {nodes.map((node, i) => {
          const done = DONE_STATES.has(node.state);
          const Content = (
            <div className="flex flex-col items-center w-28 sm:w-32 shrink-0" data-testid={`${testIdPrefix}-node-${node.stage_key}`}>
              <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium shrink-0 transition-colors ${CIRCLE_STYLE[node.state] || CIRCLE_STYLE.available}`}>
                {done ? <Check size={18} /> : node.state === "locked" ? <Lock size={15} /> : i + 1}
              </div>
              <p className={`text-sm font-medium mt-2 text-center truncate w-full ${LABEL_STYLE[node.state] || "text-ink"}`}>{node.label}</p>
              {node.helper_text && <p className="text-xs text-ink-soft/70 text-center truncate w-full">{node.helper_text}</p>}
            </div>
          );
          return (
            <div key={node.stage_key} className="flex items-start">
              {onNodeClick ? (
                <button onClick={() => onNodeClick(node)} className="hover:opacity-80 transition-opacity">{Content}</button>
              ) : (
                Content
              )}
              {i < nodes.length - 1 && (
                <div className={`h-[2px] w-8 sm:w-12 mt-[22px] shrink-0 ${done ? LINE_DONE : "bg-ink/15"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
